import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { FbaBatchStatus, FbaShipmentStatus, TransportType, getBatchStepOld, getShipmentStep } from "@boxem/core-api-schema";

import styles from "../shipments-table.module.css";

import { formatValue } from "@/utils/formatUtils";
import { formatISODate } from "@/utils/dateUtils";
import { getBatchStep } from "@/app/fba/utils";
import { getBatchesForIncompleteTable, getBillOfLadingV2, markShipmentAsShipped } from "@/api/fulfillment";
import { FbaBatch, useShipmentContext } from "@/contexts/ShipmentContext";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { LocalStorageKeys, getLocalStorageItem } from "@/utils/localStorageUtils";
import { Table, TableColumn, TableState } from "@/components/Table/Table";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import Skeleton from "@/components/Skeleton/Skeleton";
import IconProgress from "@/components/icons/IconProgress";
import Button from "@/components/forms/Button/Button";
import IconDownloadBox from "@/components/icons/IconDownloadBox";
import IconInfo from "@/components/icons/IconInfo";
import ShipmentsZeroState from "@/components/ShipmentsZeroState/ShipmentsZeroState";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import Flex from "@/components/Flex/Flex";
import Pagination from "@/components/Pagination/Pagination";
import ConfirmModal from "@/components/ConfirmModal/ConfirmModal";
import SearchInput from "@/components/SearchInput";

interface IncompleteShipmentsTableState {
  sortedColumnId: string | undefined;
  sortDirection: 'asc' | 'desc' | undefined;
  page: number;
  limit: number;
}

type IncompleteShipmentTableRecord = {
  id: number;
  name: string;
  type: "batch" | "shipment",
  shipmentType: TransportType;
  status?: string;
  shipmentStatus?: FbaShipmentStatus;
  shipmentStep?: number;
  batchStep: number;
  totalUnits: number;
  totalSkus: number;
  createdAt: Date;
  index: number;
  shipmentId?: string;
  version?: string;
};

type IncompleteShipmentsTableProps = {};

const IncompleteShipmentsTable = ({}: IncompleteShipmentsTableProps) => {
  const [location, navigate] = useLocation();
  const { createShipment } = useShipmentContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const [filteredShipments, setFilteredShipments] = useState<IncompleteShipmentTableRecord[]>([]);
  const [searchInputText, setSearchInputText] = useState<string>("");
  const [bolModalOpen, setBOLModalOpen] = useState<boolean>(false);
  const [bolLoading, setBolLoading] = useState<number>(0);
  const tableName = "incompleteShipmentsTable";
  const [batches, setBatches] = useState<FbaBatch[]>([]);
  const [batchesLoading, setBatchesLoading] = useState<boolean>(true);
  const [tableState, setTableState] = useState<IncompleteShipmentsTableState>(() => {
    const tableProfile: TableState = getLocalStorageItem(`${LocalStorageKeys.tableStatePrefix}${tableName}`);

    return {
      sortedColumnId: tableProfile?.sortedColumnId ?? undefined,
      sortDirection: tableProfile?.sortDirection ?? undefined,
      page: 1,
      limit: 10,
    };
  });

  if(batchesLoading) {
    getBatchesForIncompleteTable().then(function(result) {
      setBatchesLoading(false);
      setBatches(result);
    });
  }
  
  const getFilteredShipments = (shipments: IncompleteShipmentTableRecord[]) => {
    const columnDataKey = columns?.find((col) => col.id === tableState.sortedColumnId)?.dataKey as string;

    const newSortedData = [...shipments].sort((a, b) => {
      if (tableState.sortedColumnId) {
        let aValue = 0;
        let bValue = 0;

        aValue = a[columnDataKey];
        bValue = b[columnDataKey];

        let sortValue = 0; // Asc default
        if(columnDataKey == "name") sortValue = (aValue as any).toLowerCase().localeCompare((bValue as any).toLowerCase())
        else sortValue = aValue > bValue ? 1 : -1;
        if(tableState.sortDirection !== 'asc') {
          if(sortValue > 0) sortValue = -1;
          else if(sortValue < 0) sortValue = 1;
        }
        return sortValue;
      }

      return 0;
    });

    // filter data based on search term in product name, sku, or asin
    if (searchInputText && newSortedData) {
      return newSortedData.filter((row) => {
        const searchTermValues = searchInputText.toLowerCase().split(" ");
        const foundItem = searchTermValues.filter(term => row.name.toLowerCase().includes(term) || row.shipmentId?.toLowerCase().includes(term));
        if (foundItem.length > 0 || searchInputText.trim() === "") return row;
      });
    } else {
      return newSortedData;
    }
  };

  const handleViewBOL = async (shipmentId: number) => {
    setBolLoading(shipmentId);
    const bol = await getBillOfLadingV2(shipmentId).finally(() => setBolLoading(0));

    if (!!bol?.url) {
      const a = document.createElement('a');
      a.href = bol?.url;
      a.target = "_blank";
      a.download = "bol.pdf";
      a.click();
    } else {
      setBOLModalOpen(true);
    }
  };

  const handleSortChange = async (columnId: string, direction: 'asc' | 'desc') => {
    setTableState({
      ...tableState,
      sortedColumnId: columnId,
      sortDirection: direction,
      page: 1,
    });
  };

  const handlePageSizeChange = async (newPageSize: number) => {
    setTableState({
      ...tableState,
      page: 1,
      limit: newPageSize,
    });
  };

  const handlePageChange = async (newPage: number) => {
    setTableState({
      ...tableState,
      page: newPage,
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchInputText(value);
    if (value && tableState.page !== 1) {
      setTableState({
        ...tableState,
        page: 1,
      });
    }
  };

  const shipmentNameIncludesSearchTerm = (name: string) => {
    const lowercaseName = name.toLocaleLowerCase();
    const searchTermLowercase = searchInputText.toLocaleLowerCase();

    if (searchInputText && !lowercaseName.includes(searchTermLowercase)) return false;
    return true;
  };

  const onClearSearch = () => {
    setSearchInputText("");
  };

  const columns: TableColumn<IncompleteShipmentTableRecord>[] = [
    {
      title: 'Name',
      dataKey: 'name',
      id: 'name',
      sortable: true,
      width: '15rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <div className={styles.shipmentItem}>
          <Tooltip
            title={row.getCellData('name')}
            placement='right'
          >
            {row.getCellData('name')}
          </Tooltip>
        </div>
      ),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-long" />
        </div>
      )
    },
    {
      title: 'Type',
      dataKey: 'shipmentType',
      id: 'type',
      sortable: true,
      width: '8rem',
      alignment: 'left',
      renderRowCell: (row) => {
        const shipmentStep = getShipmentStep(row.getCellData('shipmentStatus'));
        const isLTL = row.getCellData('shipmentType') === "LTL" && row.getCellData('type') === "shipment" && shipmentStep >= 4;

        return (
          <div className={styles.shipmentItem}>
            {row.getCellData('shipmentType')}
            {isLTL && (
              <div className={styles.bolButtonContainer}>
                <Button type="primary" onClick={() => handleViewBOL(Number(row.getCellData('shipmentId')))}>
                  BOL
                  <div className={styles.downloadIcon}>
                    {bolLoading === Number(row.getCellData('shipmentId')) ? <LoadingSpinner className={styles.bolSpinner} size="1rem" /> : <IconDownloadBox />}
                  </div>
                </Button>
              </div>
            )}
          </div>
        );
      },
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-short" />
        </div>
      )
    },
    {
      title: 'Step',
      dataKey: 'step',
      id: 'step',
      sortable: false,
      width: '8rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <div className={styles.shipmentItem}>
          {row.getCellData('version') === '1' ? (
            <Flex align="center" gap={2}>
              {((row.getCellData('type') === "shipment" && row.getCellData('batchStep') === 2) || (row.getCellData('type') === "batch" && row.getCellData('batchStep') === 1)) ? (
                <>
                  <IconProgress progress={row.getCellData('batchStep')} />
                  Step {row.getCellData('batchStep')}
                </>
              ) : (
                <>
                  <IconProgress progress={row.getCellData('shipmentStep')} />
                  {row.getCellData('shipmentStep') === -1 ? "External" : `Step ${row.getCellData('shipmentStep')}`}
                </>
              )}
            </Flex> 
          ) : (
            <Flex align="center" gap={1}>
              {((row.getCellData('type') === "shipment" && row.getCellData('batchStep') === 2) || (row.getCellData('type') === "batch" && row.getCellData('batchStep') <= 3) ? (
                <>
                  <IconProgress progress={row.getCellData('batchStep')} />
                  Step {row.getCellData('batchStep')}
                </>
              ) : (
                <>
                  <IconProgress progress={row.getCellData('shipmentStep')} />
                  {row.getCellData('shipmentStep') === -1 ? "External" : `Step ${row.getCellData('shipmentStep')}`}
                </>
              ))}
            </Flex>
          )}
        </div>
      ),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-short" />
        </div>
      )
    },
    {
      title: 'Units',
      dataKey: 'totalUnits',
      id: 'totalUnits',
      sortable: true,
      width: '5rem',
      alignment: 'left',
      renderRowCell: (row) => formatValue(row.getCellData('totalUnits'), 'number'),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-short" />
        </div>
      )
    },
    {
      title: 'SKUs',
      dataKey: 'totalSkus',
      id: 'totalSkus',
      sortable: true,
      width: '5rem',
      alignment: 'left',
      renderRowCell: (row) => formatValue(row.getCellData('totalSkus'), 'number'),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-short" />
        </div>
      )
    },
    {
      title: 'Created At',
      dataKey: 'createdAt',
      id: 'createdAt',
      sortable: true,
      width: '11.5rem',
      alignment: 'left',
      renderRowCell: (row) => formatISODate(row.getCellData('createdAt')?.toString()),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-short" />
        </div>
      )
    },
    {
      title: 'Version',
      dataKey: 'version',
      id: 'version',
      sortable: true,
      width: '5rem',
      alignment: 'left',
      renderRowCell: (row) => (
        <div className={styles.shipmentItem}>
          {row.getCellData('version') === "2" ? ("New") : (
            <Tooltip
              title={"This shipment was created using version 1 of the SPAPI, which has been removed by Amazon on January 21st 2025. It can't be resumed."}
              children={<Flex gap={1}><IconInfo/>Old</Flex>}
            />
          )}
        </div>
      ),
      renderSkeleton: () => (
        <div className={styles.shipmentItem}>
          <Skeleton type="line-short" />
        </div>
      )
    },
    {
      title: '',
      dataKey: undefined,
      id: 'actions',
      sortable: false,
      pinned: 'right',
      alignment: 'right',
      width: '10rem',
      renderRowCell: (row) => {
        const shipment: IncompleteShipmentTableRecord = row.rowData;
        const batch: FbaBatch|undefined = batches.find(b => b.batch.id === shipment.id)?.batch;
        return (
        <div className={styles.shipmentItem}>
          <Flex justify="end" align="center" gap={2}>
            {row.getCellData('shipmentStatus') === FbaShipmentStatus.TRANSPORT_CONFIRMED && (
              <Button
                disableWhileOnClick
                onClick={() => {
                  return markShipmentAsShipped(Number(row.getCellData('shipmentId')))
                    .then(result => {
                      const batch: FbaBatch|undefined = batches.find(b => b.batch.id === row.getCellData('id'))?.batch;
                      if (!batch) throw []; // hacky way to just propagate to the catch block below
                      const _shipment = batch.shipments.find(s => s.id === Number(row.getCellData('shipmentId')));
                      if (!_shipment) throw [];
                      _shipment.status = result.status;
                      setBatches(batches);
                    })
                    .catch(errors => {
                      if (handleErrorResponseGenerically(errors)) {
                        return;
                      }
                    });
                }}
              >
                Mark as shipped
              </Button>
            )}
            {row.getCellData('version') === "2" && (
              <a href={`/fba/shipments/${batch.id}?return=${encodeURIComponent(location)}${shipment.shipmentId ? `&shipment=${shipment.shipmentId}` : ""}`}>
                <Button type="secondary">Resume</Button>
              </a>
            )}
          </Flex>
        </div>
      )},
    },
  ];

  useEffect(() => {
    if (batches.length > 0) {
      // For incomplete shipments, these are associated with a batch, and sometimes they are just a batch (steps 1-2),
      // so we just use batches.
      const newIncompleteShipments: IncompleteShipmentTableRecord[] = [];
      for (const b of batches) {
        const batch = b.batch;
        if (batch.status === FbaBatchStatus.BATCH_CANCELLED) {
          continue;
        }
        
        // @NOTE(michaelp): THIS IS A HUMAN READABLE BATCH STEP (+1), NOT ZERO INDEXED!!!!
        const batchStep = batch.version === '2' ? (getBatchStep({status: batch.status, shipmentType: batch.shipmentType, placementOptionsStatusesFromIncompleteBatchesDTO: b.placementOptionsStatusesFromIncompleteBatchesDTO }) + 1) : getBatchStepOld(batch.status);
        const stepToCheck = batch.version === '2' ? 3 : 1;
        if (batchStep <= stepToCheck && shipmentNameIncludesSearchTerm(batch.name)) {
          let totalUnits = 0;
          let totalSkus = 0;
          for (const item of batch.items) {
            totalUnits += item.quantity;
            totalSkus += 1;
          }

          newIncompleteShipments.push({
            id: batch.id,
            type: "batch",
            status: batch.status,
            batchStep,
            shipmentType: batch.shipmentType,
            name: batch.name,
            totalSkus,
            totalUnits,
            createdAt: batch.createdAt,
            index: 0,
            shipmentId: null,
            version: batch.version,
          });
        } else {
          const shipments = batch.shipments.filter(shipment => shipment.status !== FbaShipmentStatus.DECLINED && shipment.status !== FbaShipmentStatus.CANCELLED && shipment.status !== FbaShipmentStatus.TRANSPORT_VOIDED);
          shipments.forEach((shipment, i) => {
            let totalUnits = 0;
            let totalSkus = 0;
            for (const item of shipment.items) {
              totalUnits += item.quantity;
              totalSkus += 1;
            }
            const name = batch.shipments.length > 1
              ? `${shipment.shipmentName} (${i+1}/${shipments.length})`
              : batch.name;
    
            const shipmentStep = batch.version === '2' ? batchStep : getShipmentStep(shipment.status) ;
            if (shipmentStep <= 4 && shipmentStep > 0 && shipmentNameIncludesSearchTerm(shipment.shipmentName)) {
              // shipments beyond step 4 are "shipped", so we dont display them here.
              newIncompleteShipments.push({
                id: batch.id,
                name,
                type: "shipment",
                status: batch.status,
                shipmentStatus: shipment.status,
                batchStep,
                shipmentStep,
                shipmentType: batch.shipmentType,
                totalSkus,
                totalUnits,
                createdAt: batch.createdAt,
                index: i,
                shipmentId: shipment.id.toString(),
                version: batch.version,
              });
            }
          });
        }
      }

      setFilteredShipments(getFilteredShipments(newIncompleteShipments));
    }
  }, [
    batchesLoading,
    batches,
    searchInputText,
    tableState.page,
    tableState.limit,
    tableState.sortDirection,
    tableState.sortedColumnId,
  ]);

  const filteredShipmentsPage = filteredShipments.slice((tableState.page - 1) * tableState.limit, tableState.page * tableState.limit);

  return (
    <>
      <Table
        data={filteredShipmentsPage}
        columns={columns}
        tableName={tableName}
        className={styles.table}
        isLoading={batchesLoading}
        skeletonRows={tableState.limit}
        sortDirection={tableState.sortDirection}
        sortedColumnId={tableState.sortedColumnId}
        onSort={handleSortChange}
        showSort
        header={
          <>
            <div className={styles.searchInputContainer}>
              <SearchInput
                searchTerm={searchInputText}
                onDebounceChange={handleSearchChange}
                placeholder="Search by shipment name or ID"
                className={styles.searchInput}
              />
            </div>
          </>
        }
        zeroState={
          (!batchesLoading && batches.length === 0 && searchInputText === "") ? (
            <ShipmentsZeroState
              type="default"
              onCreateShipment={createShipment}
            />
          ) : undefined
        }
        noResultsState={
          (!batchesLoading && filteredShipments?.length === 0 && searchInputText !== "") ? (
            <ShipmentsZeroState
              type="search"
              onClearSearch={onClearSearch}
            />
          ) : undefined
        }
        footer={
          <Pagination
            total={filteredShipments.length || 0}
            pageSize={tableState.limit}
            onChangePageSize={handlePageSizeChange}
            page={tableState.page}
            onChangePage={handlePageChange}
            pageSizeOptions={[10, 20, 50]}
          />
        }
      />

      <ConfirmModal
        title={"BOL Not Ready"}
        body={<p style={{margin:"20px", padding:"20px"}}>The bill of lading for this shipment is not ready yet - the BOL should be generated 1 day before the day of pick up.</p>} 
        open={!!bolModalOpen}
        onClose={(_: boolean): void => setBOLModalOpen(false)}
      />
    </>
  );
};

export default IncompleteShipmentsTable;